import { ADDITIONAL_FEATURE_KEYS, getSalesComparisonRows } from '../constants.ts';
import { SalesComparisonTablePages } from '../components/SalesComparisonTablePages.tsx';
import {
  AdditionalFeatureKey,
  SalesComparisonGridColumnData,
  SalesComparisonPageProps,
} from '../types.ts';
import { getSalesComparisonNestedValue } from '../utils/getSalesComparisonNestedValue.ts';

export const SalesComparisonPage = ({
  subjectAddress,
  subjectSalesComparisonData,
  subjectArvSalesComparisonData,
  comparables,
  hasArvOption,
  includeTotalCostToCure,
  includeCostApproach,
  salesComparisonApproach,
  orderFeatures,
}: SalesComparisonPageProps) => {
  const rows = getSalesComparisonRows(salesComparisonApproach);
  const compsPerPage = 3;
  const pages: SalesComparisonGridColumnData[][] = [];
  const aivPages: SalesComparisonGridColumnData[][] = [];
  const arvPages: SalesComparisonGridColumnData[][] = [];
  const noTypeComps = comparables.filter((comp) => !comp.compType);
  const aivComps = comparables.filter((comp) => comp.compType === 'aiv');
  const arvComps = comparables.filter((comp) => comp.compType === 'arv');
  for (let i = 0; i < noTypeComps.length; i += compsPerPage) {
    pages.push(noTypeComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < aivComps.length; i += compsPerPage) {
    aivPages.push(aivComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < arvComps.length; i += compsPerPage) {
    arvPages.push(arvComps.slice(i, i + compsPerPage));
  }

  const filteredRows = rows.filter((field) => {
    if (!includeTotalCostToCure && field.adjustment === 'totalCostToCureAdjustment') {
      return false;
    }

    let properties = [];
    if (!hasArvOption) {
      properties = [subjectSalesComparisonData, ...comparables];
    } else {
      properties = [subjectSalesComparisonData, subjectArvSalesComparisonData, ...comparables];
    }

    const cellValues = field.key.flatMap((fieldPath) =>
      properties.map((comp) => getSalesComparisonNestedValue(comp, fieldPath)),
    );

    if (field.adjustment === 'manualCostAdjustment') {
      if (!includeCostApproach) return false;
      if (orderFeatures.includes('manualCost')) return true;
      return cellValues.some((v) => v && v !== 'None');
    }

    const nbOfFieldsWithValue = cellValues.filter((v) => !!v).length;

    if (
      nbOfFieldsWithValue === 0 &&
      field.key.some(
        (fieldPath) =>
          ADDITIONAL_FEATURE_KEYS.includes(fieldPath as AdditionalFeatureKey) ||
          fieldPath === 'otherAdjustmentsDisplayName',
      )
    ) {
      return false;
    }
    return true;
  });

  if (!hasArvOption) {
    return (
      <>
        {pages.map((chunk, pageIndex) => (
          <SalesComparisonTablePages
            key={`sales-comparison-page-${pageIndex}`}
            subjectAddress={subjectAddress}
            subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
            chunk={chunk}
            filteredRows={filteredRows}
            pageIndex={pageIndex}
            pageKeyPrefix="sales-comparison"
          />
        ))}
      </>
    );
  }

  return (
    <>
      {aivPages.map((chunk, pageIndex) => (
        <SalesComparisonTablePages
          key={`sales-comparison-aiv-page-${pageIndex}`}
          subjectAddress={subjectAddress}
          subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
          chunk={chunk}
          filteredRows={filteredRows}
          pageIndex={pageIndex}
          pageKeyPrefix="sales-comparison-aiv"
          compType="aiv"
          badge="aiv"
        />
      ))}
      {arvPages.map((chunk, pageIndex) => (
        <SalesComparisonTablePages
          key={`sales-comparison-arv-page-${pageIndex}`}
          subjectAddress={subjectAddress}
          subject={subjectArvSalesComparisonData as SalesComparisonGridColumnData}
          chunk={chunk}
          filteredRows={filteredRows}
          pageIndex={pageIndex}
          pageKeyPrefix="sales-comparison-arv"
          compType="arv"
          badge="arv"
        />
      ))}
    </>
  );
};
